import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import SlidingMenu from '../components/SlidingMenu';
import { formatDateWithLocale } from '../shared/utils';
import VersionFooter from '../components/VersionFooter';
import { clog, cerror } from '../shared/consoleLogger';
import { getCachedAccountData } from '../shared/accountCache';
import { CONSTANTS } from '../shared/constants';

export default function Plans() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isFreeTier, setIsFreeTier] = useState(false);
  
  const navigate = useNavigate();  

  // Fetch user plans when component mounts
  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist
  useEffect(() => {
    fetchUserTier();
    fetchUserPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserTier = async () => {
    try {
      const account = await getCachedAccountData();
      const tier = account?.subscription?.tier || 'free';
      setIsFreeTier(tier !== 'admin' && tier !== 'pro');
    } catch (err) {
      cerror('Error fetching user tier:', err);
      setIsFreeTier(true); // Default to free on error
    }
  };

  const fetchUserPlans = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Always use POST method, even for GET-like operations
      const response = await apiCall("/api/model?action=" + ApiActions.GET_PLANS, {}, 'POST');
      
      if (response.success) {
        setPlans(response.plans);
      } else {
        setError(response.message || t('plans.failedToLoad'));
      }
    } catch (err) {
      cerror('Error fetching user plans:', err);
      setError(t('plans.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = () => {
    navigate('/trip-creator');
  };


  const confirmDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Using POST method for delete operation
      const response = await apiCall("/api/model?action=" + ApiActions.DELETE_PLAN,
        { planId: planToDelete.id },
        'POST'
      );
      
      if (response.success) {
        // Remove the deleted plan from state
        setPlans(plans.filter(plan => plan.id !== planToDelete.id));
        setShowDeleteModal(false);
        setPlanToDelete(null);
      } else {
        setError(response.message || t('plans.failedToDelete'));
      }
    } catch (err) {
      cerror('Error deleting plan:', err);
      setError(t('plans.errorDeleting'));
    } finally {
      setIsDeleting(false);
    }
  };


  // Helper function to sort plans by start date descending
  const sortPlansByStartDate = (plansArray) => {
    return [...plansArray].sort((a, b) => {
      // Use startDate directly from plan object
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      // Descending order (newest first)
      return dateB - dateA;
    });
  };

  // Group plans by date status
  const groupPlansByDate = (plans) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    const current = [];
    const future = [];
    const past = [];

    plans.forEach(plan => {
      // Use startDate and endDate directly from plan object
      const startDate = new Date(plan.startDate);
      const endDate = plan.endDate ? new Date(plan.endDate) : null;

      // Reset time to start of day for comparison
      startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(0, 0, 0, 0);

      if (endDate) {
        // Has both dates
        if (startDate <= today && endDate >= today) {
          current.push(plan);
        } else if (startDate > today) {
          future.push(plan);
        } else {
          past.push(plan);
        }
      } else {
        // Only start date
        if (startDate <= today) {
          current.push(plan);
        } else {
          future.push(plan);
        }
      }
    });

    // Sort each group by start date descending
    return {
      current: sortPlansByStartDate(current),
      future: sortPlansByStartDate(future),
      past: sortPlansByStartDate(past)
    };
  };

  // Render plan card component
  const renderPlanCard = (plan) => {
    return (
      <Col md={4} key={plan.id} className="mb-4">
        <Card className="h-100 shadow-sm">
          {plan.coverImage && (
            <Card.Img 
              variant="top" 
              src={plan.coverImage}
              alt={plan.name}
              style={{ height: '180px', objectFit: 'cover' }}
              onError={(e) => {
                clog('Image failed to load:', e.target.src);
                e.target.style.display = 'none';
                e.target.onerror = null;
              }}
            />
          )}
          <Card.Body>
            <Card.Title>{plan.name}</Card.Title>
            <Card.Text>
              <Badge bg="secondary" className="me-2">
                {t('plans.created')} {formatDateWithLocale(plan.createdDate, 'PPP')}
              </Badge>
              {/* Show "Shared to Group" badge if owner has shared this plan */}
              {plan.sharedWithGroups && plan.sharedWithGroups.length > 0 && (
                <Badge bg="success" className="me-2">
                  <i className="bi bi-people me-1"></i>
                  {t('plans.sharedToGroup')}
                </Badge>
              )}
              {/* Show "Shared with Me" badge if this is a shared plan (has ownerPlanSharedData with originalPlanId) - but NOT if it's the owner's plan */}
              {plan.ownerPlanSharedData && plan.ownerPlanSharedData.originalPlanId && (!plan.sharedWithGroups || plan.sharedWithGroups.length === 0) && (
                <Badge bg="info" className="me-2">
                  <i className="bi bi-share me-1"></i>
                  {t('plans.sharedWithMe')}
                </Badge>
              )}
              {/* Show "Update Available" badge if updateExists is true */}
              {plan.ownerPlanSharedData && plan.ownerPlanSharedData.updateExists && (
                <Badge bg="warning" className="me-2">
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {t('plans.updateAvailable')}
                </Badge>
              )}
            </Card.Text>
            {plan.startDate && plan.endDate && (
              <div className="text-muted small mb-2">
                {formatDateWithLocale(plan.startDate, 'PPP')} - {formatDateWithLocale(plan.endDate, 'PPP')}
              </div>
            )}
          </Card.Body>
          <Card.Footer className="bg-white">
            <div className="d-flex justify-content-between">
              <Button 
                variant="outline-primary" 
                className="me-2 flex-grow-1"
                onClick={() => {
                  if (isFreeTier) {
                    const sortedPlans = [...plans].sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
                    const firstThreeIds = sortedPlans.slice(0, CONSTANTS.LIMITS.PLANS_LIMIT).map(p => p.id);
                    if (!firstThreeIds.includes(plan.id)) {
                      setShowUpgradeModal(true);
                      return;
                    }
                  }
                  navigate(`/plan-detail/${plan.id}`);
                }}
              >
                {t('plans.viewTrip')}
              </Button>
              <Button 
                variant="outline-danger"
                onClick={() => confirmDeletePlan(plan)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </Col>
    );
  };

  // Render plan group section
  const renderPlanGroup = (title, plans, icon) => {
    if (plans.length === 0) return null;

    return (
      <div className="mb-5">
        <h3 className="mb-3">
          <i className={`bi ${icon} me-2`}></i>
          {title} <Badge bg="secondary">{plans.length}</Badge>
        </h3>
        <Row>
          {plans.map(plan => renderPlanCard(plan))}
        </Row>
      </div>
    );
  };

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist

  return (
    <div className="plans-page">
      <SlidingMenu title={t('plans.myTrips')} />

      <Container className="py-4">
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <Row className="mb-4">
          <Col className="d-flex justify-content-center align-items-center">
            <Button 
              variant="primary" 
              onClick={handleCreatePlan}
            >
              <i className="bi bi-plus-circle me-2"></i>
              {t('plans.createNewTrip')}
            </Button>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('plans.loadingTrips')}</p>
          </div>
        ) : plans.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <h3 className="mb-3">{t('plans.noTripsYet')}</h3>
              <p className="text-muted mb-4">
                {t('plans.noTripsDescription')}
              </p>
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleCreatePlan}
              >
                <i className="bi bi-robot me-2"></i>
                {t('plans.createNewTrip')}
              </Button>
            </Card.Body>
          </Card>
        ) : (() => {
          const { current, future, past } = groupPlansByDate(plans);
          
          return (
            <>
              {renderPlanGroup(t('plans.currentOngoingTrips'), current, 'bi-calendar-check')}
              {renderPlanGroup(t('plans.futureTrips'), future, 'bi-calendar-event')}
              {renderPlanGroup(t('plans.pastTrips'), past, 'bi-calendar-x')}
            </>
          );
        })()}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('plans.deleteTrip')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('plans.deleteConfirm')} <strong>{planToDelete?.name}</strong>{t('plans.deleteConfirmEnd')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeletePlan}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('plans.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Upgrade Required Modal */}
        <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-lock-fill text-warning me-2"></i>
              {t('planDetail.upgrade.upgradeRequired')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <p className="mb-0 fs-5">
              {t('planDetail.upgrade.freeTierLimit', { limit: CONSTANTS.LIMITS.PLANS_LIMIT })}
            </p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button 
              variant="primary" 
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/pricing');
              }}
            >
              <i className="bi bi-star-fill me-2"></i>{t('planDetail.upgrade.viewPricing')}
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowUpgradeModal(false)}
            >
              {t('planDetail.upgrade.maybeLater')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      
      <VersionFooter />
    </div>
  );
} 